import React, { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { random } from 'lodash';
import { useConst } from '@fiverr-private/hooks';
import { INT_32_MAX, INT_32_MIN } from '../../../constants/constraints';
import { trackGridEndOfFeed } from '../../../utils/events/mixpanel/events/gridEndOfFeed';
import { InspireGrid } from '../../InspireGrid';
import { InspireGridSkeleton } from '../../internal/InspireGridSkeleton';
import { useRecommendedInspireDeliveries } from '../hooks/useRecommendedInspireDeliveries';
import {
  DEFAULT_SKIP,
  DEFAULT_RELATED_SUB_CATEGORY_IDS,
  DEFAULT_INSPIRE_DELIVERY_IDS_SAVED,
  DEFAULT_SHOULD_HIDE_SAVED_DELIVERIES,
  DEFAULT_MIXPANEL_RECOMMENDATIONS_ENABLED,
} from '../constants';
import { DEFAULT_CHUNK_SIZE, DEFAULT_MAX_CHUNKS_COUNT } from './constants';
import { ShowMore } from './ShowMore';
import type { RecommendationsInspireGridProps } from './types';

import classes from './RecommendationsInspireGrid.module.scss';

export const RecommendationsInspireGrid = ({
  skip = DEFAULT_SKIP,
  relatedSubCategoryIds = DEFAULT_RELATED_SUB_CATEGORY_IDS,
  inspireDeliveryIdsSaved = DEFAULT_INSPIRE_DELIVERY_IDS_SAVED,
  shouldHideSavedDeliveries = DEFAULT_SHOULD_HIDE_SAVED_DELIVERIES,
  mixpanelRecommendationsEnabled = DEFAULT_MIXPANEL_RECOMMENDATIONS_ENABLED,
  mixpanelRecommendationsOptions,
  chunkSize = DEFAULT_CHUNK_SIZE,
  maxChunksCount = DEFAULT_MAX_CHUNKS_COUNT,
  ...rest
}: RecommendationsInspireGridProps) => {
  if (mixpanelRecommendationsEnabled && !mixpanelRecommendationsOptions) {
    throw new TypeError(
      'RecommendationsInspireGrid: must provide "mixpanelRecommendationsOptions" when [mixpanelRecommendationsEnabled=true]'
    );
  }

  const seed = useConst(() => random(INT_32_MIN, INT_32_MAX));
  const [cursor, setCursor] = useState<string>();

  const {
    inspireDeliveries: inspireDeliveriesRecommended,
    endCursor,
    isLoading,
    chunksCount,
  } = useRecommendedInspireDeliveries(
    {
      relatedSubCategoryIds,
      seed,
      amount: chunkSize,
      cursor,
    },
    skip
  );

  const hasMore = chunksCount < maxChunksCount;
  const isInitialLoading = isLoading && chunksCount === 0;

  const loadMore = () => {
    if (hasMore) {
      setCursor(endCursor);
    }
  };

  const inspireDeliveriesFiltered = useMemo(() => {
    return inspireDeliveriesRecommended.filter(({ id }) => !inspireDeliveryIdsSaved.includes(id));
  }, [inspireDeliveriesRecommended, inspireDeliveryIdsSaved]);

  const inspireDeliveries = shouldHideSavedDeliveries ? inspireDeliveriesFiltered : inspireDeliveriesRecommended;

  const onSeenAll = useCallback(() => {
    if (!mixpanelRecommendationsEnabled) {
      return;
    }

    const pageName = mixpanelRecommendationsOptions?.pageName || '';
    const carouselName = mixpanelRecommendationsOptions?.carouselName || '';

    trackGridEndOfFeed({
      pageName,
      carouselName,
    });
  }, [mixpanelRecommendationsEnabled, mixpanelRecommendationsOptions]);

  const seenAllRef = useRef(false);
  useEffect(() => {
    if (!hasMore && !isLoading && !seenAllRef.current) {
      seenAllRef.current = true;
      onSeenAll();
    }
  }, [onSeenAll, hasMore, isLoading]);

  if (isInitialLoading) {
    return <InspireGridSkeleton count={chunkSize} />;
  }

  return (
    <div className={classes.container}>
      <InspireGrid inspireDeliveries={inspireDeliveries} {...rest} />
      <ShowMore isLoading={isLoading} hasMore={hasMore} loadMore={loadMore} />
    </div>
  );
};
